import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, Coins, Eye, History, IndianRupee, Pencil, PiggyBank, Wallet } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ImagePreview } from '@/components/ImagePreview';
import { StatusBadge } from '@/components/StatusBadge';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import { ColumnFilter } from '@/components/ColumnFilter';
import { CardNo, LoanLink, MemberLink, PolicyLink } from '@/components/links/RecordLinks';
import { useDrawers } from '@/contexts/DrawerContext';
import type { LastPayment, Member, MemberBalanceSummary, Policy } from '@/types';
import { formatCurrency, formatPaymentDate, isPaidByPmsThisMonth, paymentTypeLabel } from '@/lib/payments';
import { formatMonthKey } from '@/lib/months';
import { canPayPolicy, getPolicyProgress } from '@/lib/policies';
import { getLoanEligibilityText } from '@/lib/loans';
import { cn, displayMemberNo } from '@/lib/utils';

/** Only the first column (ID) can be sorted. */
export type SortField = 'id';
export type SortDirection = 'asc' | 'desc';

export interface ColumnSort {
  field: SortField;
  direction: SortDirection;
}

/** One table row: a member's first policy (the member row) or one of their other policies. */
export interface PolicyRow {
  member: Member;
  policy: Policy;
  /** The member's balance; shown on the member row only. */
  balance: MemberBalanceSummary;
  isMemberRow: boolean;
  /** 1-based position of the policy for this member. */
  position: number;
  policyCount: number;
  isOpen: boolean;
  isLastChild: boolean;
}

export interface RowActions {
  /** Show or hide a member's other policies. */
  onToggle: (memberId: string) => void;
}

export interface TableColumn {
  id: string;
  className?: string;
  renderHeader: () => ReactNode;
  renderCell: (row: PolicyRow) => ReactNode;
}

const HeaderLabel = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={cn('text-xs font-semibold tracking-wide text-gray-800', className)}>{children}</span>
);

function SortButton({
  field,
  sort,
  onSort,
  children,
}: {
  field: SortField;
  sort: ColumnSort | null;
  onSort: (field: SortField) => void;
  children: ReactNode;
}) {
  const isActive = sort?.field === field;
  const Icon = !isActive ? ArrowUpDown : sort?.direction === 'asc' ? ArrowUp : ArrowDown;

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-2 h-8 gap-1 px-2 text-xs font-semibold tracking-wide text-gray-800 hover:bg-gray-100"
      onClick={() => onSort(field)}
    >
      {children}
      <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-[#4369a0]' : 'text-gray-400')} />
    </Button>
  );
}

/** Two short labelled lines, e.g. "Policy: Paid" / "Loan: Not taken". */
function TwoLine({ policy, loan }: { policy: ReactNode; loan: ReactNode }) {
  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-x-2.5 gap-y-1 text-[13px]">
      <span className="text-xs text-gray-500">Policy</span>
      {policy}
      <span className="text-xs text-gray-500">Loan</span>
      {loan}
    </div>
  );
}

const MONTH_TEXT: Record<string, { label: string; className: string }> = {
  paid: { label: 'Paid', className: 'text-teal-700' },
  unpaid: { label: 'Unpaid', className: 'text-amber-700' },
  no_dues: { label: 'No Dues', className: 'text-teal-700' },
  closed: { label: 'Closed', className: 'text-gray-500' },
  not_due: { label: 'Not Due', className: 'text-gray-400' },
  not_taken: { label: 'Not Taken', className: 'text-gray-400' },
};

/** "Paid", "Unpaid"… for this month; "Paid by PMS" when PMS paid it this month. */
function MonthText({ status, lastPayment }: { status?: string; lastPayment?: LastPayment | null }) {
  if (status === 'paid' && isPaidByPmsThisMonth(lastPayment)) return <span className="text-[#4369a0]">Paid by PMS</span>;
  const item = MONTH_TEXT[status ?? 'not_taken'] ?? MONTH_TEXT.not_taken;
  return <span className={item.className}>{item.label}</span>;
}

/** The member's Paid by PMS balance; clicking it opens the Member Balance drawer. */
function BalanceCell({ member, balance }: { member: Member; balance: MemberBalanceSummary }) {
  const { openDrawer } = useDrawers();
  if (!balance.hasHistory) return <span className="text-xs text-gray-400">No Balance</span>;
  const open = () => openDrawer('memberBalance', member);
  if (balance.balanceDue <= 0) {
    return (
      <button type="button" onClick={open} className="text-xs text-gray-500 hover:text-[#4369a0] hover:underline">
        Cleared
      </button>
    );
  }
  return (
    <div>
      <button type="button" onClick={open} className="text-[13px] font-semibold tabular-nums text-amber-900 hover:underline">
        {formatCurrency(balance.balanceDue)}
      </button>
      <p className="whitespace-nowrap text-xs text-gray-500">
        {balance.interestDue ? `Incl. ${formatCurrency(balance.interestDue)} interest` : '12% / year'}
      </p>
    </div>
  );
}

/** The "⋯" menu of a policy row. */
function PolicyRowMenu({ row }: { row: PolicyRow }) {
  const { openDrawer } = useDrawers();
  const { member, policy } = row;
  return (
    <RowActionsMenu
      label={`Actions for ${policy.policyNo}`}
      actions={[
        { label: 'Policy Details', icon: Eye, onSelect: () => openDrawer('policyDetails', member, policy.id) },
        {
          label: 'Edit Policy',
          icon: Pencil,
          disabled: policy.status === 'closed',
          onSelect: () => openDrawer('editPolicy', member, policy.id),
        },
        {
          label: policy.status === 'closed' ? 'Policy Closed' : 'Policy Payment',
          icon: IndianRupee,
          disabled: !canPayPolicy(policy),
          onSelect: () => openDrawer('policyPayment', member, policy.id),
        },
        { label: 'Policy Loan', icon: Wallet, onSelect: () => openDrawer('policyLoan', member, policy.id) },
        { label: 'Savings & Close', icon: PiggyBank, onSelect: () => openDrawer('policySavings', member, policy.id) },
        { label: 'Member Balance', icon: Coins, onSelect: () => openDrawer('memberBalance', member) },
        // The member's full history: only on the member row, the rows below belong to the same member.
        ...(row.isMemberRow
          ? [{ label: 'Transaction History', icon: History, onSelect: () => openDrawer('memberTransactions', member) }]
          : []),
      ]}
    />
  );
}

function LastPaymentText({ payment, emptyText }: { payment?: LastPayment | null; emptyText: string }) {
  if (!payment) return <span className="text-gray-400">{emptyText}</span>;
  return (
    <span className="whitespace-nowrap text-gray-700">
      {formatPaymentDate(payment.date)} <span className="text-gray-400">· {paymentTypeLabel(payment.type)}</span>
    </span>
  );
}

function ProgressCell({ title, percent, detail }: { title: ReactNode; percent: number; detail: ReactNode }) {
  return (
    <div className="min-w-[150px] space-y-1.5">
      <div className="text-[13px] font-semibold tabular-nums text-gray-800">{title}</div>
      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-[#4369a0] transition-all" style={{ width: `${percent}%` }} />
      </div>
      <div className="whitespace-nowrap text-xs tabular-nums text-gray-500">{detail}</div>
    </div>
  );
}

const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase();

/** Member cell: the member on their first policy row, tree lines on the other policy rows. */
function MemberCell({ row, actions }: { row: PolicyRow; actions: RowActions }) {
  if (!row.isMemberRow) {
    return (
      <div className="relative flex h-full min-h-10 items-center">
        <span className={cn('absolute left-[17px] top-[-14px] w-0.5 bg-gray-300', row.isLastChild ? 'h-[calc(50%+14px)]' : 'bottom-[-14px]')} />
        <span className="absolute left-[17px] top-1/2 h-0.5 w-6 bg-gray-300" />
        <span className="absolute left-[41px] top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border-2 border-gray-400 bg-white" />
        <span className="pl-14 text-xs text-gray-500">
          Policy {row.position} of {row.policyCount}
        </span>
      </div>
    );
  }

  const { member } = row;
  const photo = member.profileImage || member.photo;

  return (
    <div className="relative flex min-w-0 items-center gap-2.5">
      {row.isOpen && <span className="absolute left-[17px] top-[calc(50%+18px)] bottom-[-14px] w-0.5 bg-gray-300" />}
      <ImagePreview src={photo} alt={member.name || 'Member photo'}>
        <Avatar className="h-9 w-9 shrink-0 border border-white shadow-sm ring-1 ring-gray-200/60">
          <AvatarImage src={photo} />
          <AvatarFallback className="bg-[#4369a0]/10 text-xs font-semibold text-[#4369a0]">
            {initials(member.name)}
          </AvatarFallback>
        </Avatar>
      </ImagePreview>
      <div className="min-w-0 flex-1">
        <MemberLink memberId={member.id} memberNo={member.memberNo} name={member.name} className="block w-full truncate text-sm font-medium text-gray-900" />
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{member.phone}</span>
          {row.policyCount > 1 && (
            <button
              type="button"
              onClick={() => actions.onToggle(member.id)}
              aria-expanded={row.isOpen}
              className="inline-flex items-center gap-0.5 rounded-full border border-[#4369a0]/30 bg-[#4369a0]/5 px-2 py-0.5 text-[11px] font-semibold text-[#2d4f73] hover:bg-[#4369a0]/10"
            >
              {row.policyCount} policies
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', row.isOpen && 'rotate-180')} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Policy Status header filter: '' = All, otherwise a policy status. */
export interface PolicyStatusFilter {
  value: string;
  onChange: (value: string) => void;
}

export const createColumns = (
  sort: ColumnSort | null,
  onSort: (field: SortField) => void,
  actions: RowActions,
  policyStatusFilter: PolicyStatusFilter
): TableColumn[] => [
  {
    id: 'id',
    className: 'w-[80px]',
    renderHeader: () => (
      <SortButton field="id" sort={sort} onSort={onSort}>
        ID
      </SortButton>
    ),
    renderCell: (row) =>
      row.isMemberRow ? (
        <span className="flex w-[13px] justify-center whitespace-nowrap tabular-nums text-gray-500 text-sm font-medium">{displayMemberNo(row.member)}</span>
      ) : null,
  },
  {
    id: 'cardNo',
    className: 'w-[90px]',
    renderHeader: () => <HeaderLabel>Card No</HeaderLabel>,
    renderCell: ({ policy }) => (
      <span className="text-[13px] text-gray-700">
        <CardNo value={policy.cardNo} />
      </span>
    ),
  },
  {
    id: 'member',
    className: 'w-[240px] max-w-[260px] pr-2',
    renderHeader: () => <HeaderLabel>Member</HeaderLabel>,
    renderCell: (row) => <MemberCell row={row} actions={actions} />,
  },
  {
    id: 'policy',
    className: 'w-[130px]',
    renderHeader: () => <HeaderLabel>Policy</HeaderLabel>,
    renderCell: ({ member, policy }) => (
      <div>
        <PolicyLink member={member} policyNo={policy.policyNo} className="text-sm font-semibold tabular-nums text-gray-800" />
        <p className="text-xs text-gray-500">Started {formatMonthKey(policy.startMonth)}</p>
      </div>
    ),
  },
  {
    id: 'status',
    className: 'w-[110px]',
    renderHeader: () => (
      <ColumnFilter
        label="Policy Status"
        options={[
          { value: 'active', label: 'Active' },
          { value: 'completed', label: 'Completed' },
          { value: 'closed', label: 'Closed' },
        ]}
        value={policyStatusFilter.value}
        onChange={policyStatusFilter.onChange}
      />
    ),
    renderCell: ({ policy }) => <StatusBadge status={policy.status} />,
  },
  {
    id: 'thisMonth',
    className: 'w-[140px]',
    renderHeader: () => <HeaderLabel>This Month</HeaderLabel>,
    renderCell: ({ policy }) => (
      <TwoLine
        policy={<MonthText status={policy.thisMonth} lastPayment={policy.lastPayment} />}
        loan={
          <MonthText
            status={policy.loan.eligibility === 'active' ? policy.loan.thisMonth : 'not_taken'}
            lastPayment={policy.loan.lastPayment}
          />
        }
      />
    ),
  },
  {
    id: 'policyProgress',
    className: 'w-[180px]',
    renderHeader: () => <HeaderLabel>Policy Progress</HeaderLabel>,
    renderCell: ({ policy }) => {
      const progress = getPolicyProgress(policy);
      return (
        <ProgressCell
          title={`${policy.paidMonths} / ${policy.totalMonths}`}
          percent={progress.percent}
          detail={
            <>
              <span className="text-gray-700">
                {formatCurrency(progress.paidAmount)} / {formatCurrency(progress.totalAmount)}
              </span>{' '}
              ({progress.leftAmount ? `${formatCurrency(progress.leftAmount)} left` : 'Fully paid'})
            </>
          }
        />
      );
    },
  },
  {
    id: 'loanProgress',
    className: 'w-[180px]',
    renderHeader: () => <HeaderLabel>Loan Progress</HeaderLabel>,
    renderCell: ({ member, policy }) => {
      const { loan } = policy;
      if (loan.eligibility !== 'active' || !loan.amount) {
        return <span className="text-xs text-gray-500">{getLoanEligibilityText(loan)}</span>;
      }
      const principalPaid = loan.principalPaid ?? 0;
      return (
        <ProgressCell
          title={loan.loanNo && <LoanLink member={member} loanNo={loan.loanNo} />}
          percent={Math.round((principalPaid / loan.amount) * 100)}
          detail={
            <>
              <span className="text-gray-700">
                {formatCurrency(principalPaid)} / {formatCurrency(loan.amount)}
              </span>{' '}
              <span className="text-amber-800">({formatCurrency(loan.outstanding ?? 0)} left)</span>
            </>
          }
        />
      );
    },
  },
  {
    id: 'lastPayment',
    className: 'w-[190px]',
    renderHeader: () => <HeaderLabel>Last Payment</HeaderLabel>,
    renderCell: ({ policy }) => (
      <TwoLine
        policy={<LastPaymentText payment={policy.lastPayment} emptyText="--" />}
        loan={
          <LastPaymentText
            payment={policy.loan.lastPayment}
            emptyText={policy.loan.eligibility === 'active' ? 'Not Due' : 'Not Taken'}
          />
        }
      />
    ),
  },
  {
    id: 'balance',
    className: 'w-[120px]',
    renderHeader: () => <HeaderLabel>Balance</HeaderLabel>,
    renderCell: (row) => (row.isMemberRow ? <BalanceCell member={row.member} balance={row.balance} /> : null),
  },
  {
    id: 'actions',
    className: 'w-[72px] text-center',
    renderHeader: () => <HeaderLabel className="block text-center">Actions</HeaderLabel>,
    renderCell: (row) => (
      <div className="flex justify-center">
        <PolicyRowMenu row={row} />
      </div>
    ),
  },
];
